import React, { useMemo } from 'react';
import KeyValueTable from './KeyValueTable';

/*
 * InauspiciousPeriodsSection — Section 4 of Pambu Sheet.
 *
 * Rows: Rahu Kaal, Yamagandam, Gulikai, Dur Muhurtam (multi-window),
 * optional Bhadra and Varjyam.
 * Times in HH:MM (no seconds — periods don't need sub-second precision).
 * Active period: red left-border accent on activeRowKey.
 */

function nowInRange(now, start, end) {
  if (!now || !start || !end) return false;
  const toMin = (t) => {
    const parts = t.split(':').map(Number);
    return (parts[0] || 0) * 60 + (parts[1] || 0);
  };
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const startMin = toMin(start);
  const endMin = toMin(end);
  if (endMin >= startMin) {
    return nowMin >= startMin && nowMin < endMin;
  }
  // overnight wrap
  return nowMin >= startMin || nowMin < endMin;
}

function fmtRange(start, end) {
  if (!start || !end) return '—';
  return `${start.substring(0, 5)} — ${end.substring(0, 5)}`;
}

function buildSingleWindow(key, labelTa, labelEn, period, now) {
  if (!period || !period.start) return null;
  return {
    key,
    labelTa,
    labelEn,
    value: fmtRange(period.start, period.end),
    active: nowInRange(now, period.start, period.end),
  };
}

function buildMultiWindow(keyPrefix, labelTa, labelEn, windows, now) {
  if (!windows || windows.length === 0) return null;
  const first = windows[0];
  const subWindows = windows.slice(1);
  const isFirstActive = nowInRange(now, first.start, first.end);
  const subRows = subWindows.map((w, idx) => ({
    key: `${keyPrefix}_sub_${idx}`,
    labelTa: '',
    labelEn: '',
    value: fmtRange(w.start, w.end),
    active: nowInRange(now, w.start, w.end),
  }));
  return {
    key: keyPrefix,
    labelTa,
    labelEn,
    value: fmtRange(first.start, first.end),
    active: isFirstActive,
    subRows,
  };
}

const InauspiciousPeriodsSection = ({ inauspicious, now, lang = 'en' }) => {
  const built = useMemo(() => {
    if (!inauspicious) return [];
    const out = [];
    const single = (k, ta, en, period) => {
      const r = buildSingleWindow(k, ta, en, period, now);
      if (r) out.push(r);
    };
    single('rahu_kaal', 'ராகு காலம்', 'Rahu Kaal', inauspicious.rahu_kaal);
    single('yamagandam', 'யமகண்டம்', 'Yamagandam', inauspicious.yamagandam);
    single('gulikai', 'குளிகை', 'Gulikai', inauspicious.gulikai);

    const dur = inauspicious.dur_muhurtam || inauspicious.durmuhurtam;
    if (Array.isArray(dur) && dur.length > 0) {
      const r = buildMultiWindow('dur_muhurtam', 'துர் முகூர்த்தம்', 'Dur Muhurtam', dur, now);
      if (r) out.push(r);
    } else if (dur && dur.start) {
      const r = buildSingleWindow('dur_muhurtam', 'துர் முகூர்த்தம்', 'Dur Muhurtam', dur, now);
      if (r) out.push(r);
    }

    if (inauspicious.bhadra && inauspicious.bhadra.start) {
      single('bhadra', 'பத்திரை', 'Bhadra', inauspicious.bhadra);
    }
    const varjyam = inauspicious.varjyam;
    if (Array.isArray(varjyam) && varjyam.length > 0) {
      const r = buildMultiWindow('varjyam', 'வர்ஜ்யம்', 'Varjyam', varjyam, now);
      if (r) out.push(r);
    } else if (varjyam && varjyam.start) {
      single('varjyam', 'வர்ஜ்யம்', 'Varjyam', varjyam);
    }
    return out;
  }, [inauspicious, now]);

  if (built.length === 0) return null;

  // find active row (top-level or sub)
  let activeKey = null;
  for (const row of built) {
    if (row.active) { activeKey = row.key; break; }
    if (row.subRows) {
      const activeSub = row.subRows.find(s => s.active);
      if (activeSub) { activeKey = activeSub.key; break; }
    }
  }

  return <KeyValueTable rows={built} activeRowKey={activeKey} lang={lang} accentTone="bad" />;
};

export default InauspiciousPeriodsSection;
